// A one-line comparison for how long someone's log actually runs. Every entry is
// a fixed historical duration in whole days, with the dates it was counted from:
// a scheduled event counts its days inclusive, anything measured between two
// moments (a journey, a streak, a build) counts the days elapsed. The seasons are
// one representative recent season, since the real length shifts a few days a
// year. Nothing here moves with today's date, so the same span always produces
// the same sentence — no clock, no randomness, nothing generated at read time.
// Sorted ascending; the selection below relies on that.
//
// Each entry is printed as a sentence about the reference itself — "<label> <verb>
// <days> days" — so the label is a noun phrase and `verb` is whatever reads for
// that kind of thing: "ran" for an event or a streak, "took" for a journey or a
// build, "takes" for the two that recur and so want the present tense. Several
// labels name one specific edition ("the 2023-24 NBA regular season") rather than
// the generic thing: the day count came from that edition, and a season's real
// length shifts a few days a year, so naming it is the honest form.
export const MILESTONES = [
  { days: 14, label: "the Wimbledon fortnight" },                          // 2024-07-01 → 07-14
  { days: 17, label: "the Paris 2024 Olympics" },                          // 2024-07-26 → 08-11
  { days: 23, label: "the 2024 Tour de France" },                          // 2024-06-29 → 07-21
  { days: 29, label: "the 2022 World Cup in Qatar" },                      // 2022-11-20 → 12-18
  { days: 46, label: "Roger Bannister's reign as mile record holder" },    // 1954-05-06 → 06-21
  { days: 51, label: "the 2023 Rugby World Cup" },                         // 2023-09-08 → 10-28
  { days: 59, label: "the 2024 NBA playoffs" },                            // 2024-04-20 → 06-17
  { days: 92, label: "Sputnik 1's flight" },                               // 1957-10-04 → 1958-01-04
  { days: 123, label: "the 2024 NFL regular season" },                     // 2024-09-05 → 2025-01-05
  { days: 146, label: "Byron Nelson's 11-win streak in 1945" },            // 1945-03-11 → 08-04
  { days: 174, label: "the 2023-24 NBA regular season" },                  // 2023-10-24 → 2024-04-14
  { days: 203, label: "Perseverance's cruise to Mars", verb: "took" },     // 2020-07-30 → 2021-02-18
  { days: 280, label: "a human pregnancy", verb: "takes" },                // 40 weeks
  { days: 283, label: "the 2023-24 Premier League season" },               // 2023-08-11 → 2024-05-19
  { days: 340, label: "Scott Kelly's year in space" },                     // 2015-03-27 → 2016-03-01
  { days: 410, label: "the build of the Empire State Building", verb: "took" }, // 1930-03-17 → 1931-05-01
  { days: 437, label: "Valeri Polyakov's record stay in space" },          // 437d 18h, the longest ever
  { days: 546, label: "Voyager 1's flight to Jupiter", verb: "took" },     // 1977-09-05 → 1979-03-05
  { days: 793, label: "the build of the Eiffel Tower", verb: "took" },     // 1887-01-28 → 1889-03-31
  { days: 1082, label: "Magellan's circumnavigation", verb: "took" },      // 1519-09-20 → 1522-09-06
  { days: 1461, label: "one Olympic cycle", verb: "takes" },               // 4 years, one leap day
  { days: 1603, label: "the build of the Golden Gate Bridge", verb: "took" }, // 1933-01-05 → 1937-05-27
  { days: 3110, label: "Rocky Marciano's 49-fight unbeaten streak" },         // 1947-03-17 → 1955-09-21
  { days: 3463, label: "New Horizons' flight to Pluto", verb: "took" },    // 2006-01-19 → 2015-07-14
  { days: 3569, label: "Edwin Moses' 122-race winning streak" },           // 1977-08-26 → 1987-06-04
  { days: 4388, label: "Voyager 2's flight to Neptune", verb: "took" },    // 1977-08-20 → 1989-08-25
  { days: 5956, label: "Cal Ripken Jr.'s consecutive-games streak" },      // 1982-05-30 → 1998-09-19
  { days: 10315, label: "the life of the Berlin Wall" }                    // 1961-08-13 → 1989-11-09
];

const MS_PER_DAY = 86400000;
// Within this factor of a reference the two read as the same length.
const ABOUT_TOLERANCE = 1.08;
const SHORTEST = MILESTONES[0].days;

function utcDayIndex(value) {
  // Guarded before the Date: `new Date(null)` is the epoch, not an invalid date.
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? Math.floor(time / MS_PER_DAY) : null;
}

// The oldest and newest imported workout, as UTC days — the viewer's timezone
// should not move the boundary of what counts as a day of logs. `days` is the
// calendar span and `trainedDays` the days actually trained on; two workouts on
// one day are one day. Both are needed: the span alone reads as if every day in
// it was a training day.
export function logSpan(workouts) {
  const trained = new Set();
  let first = null;
  let last = null;
  for (const workout of workouts || []) {
    const day = utcDayIndex(workout?.startTime);
    if (day === null) continue;
    trained.add(day);
    if (first === null || day < first) first = day;
    if (last === null || day > last) last = day;
  }
  if (first === null) return null;
  return {
    firstDate: new Date(first * MS_PER_DAY).toISOString().slice(0, 10),
    lastDate: new Date(last * MS_PER_DAY).toISOString().slice(0, 10),
    days: last - first + 1,
    trainedDays: trained.size
  };
}

export function pickMilestone(days) {
  if (!Number.isFinite(days) || days < SHORTEST) return null;

  let nearest = null;
  for (const reference of MILESTONES) {
    const distance = Math.abs(Math.log(days / reference.days));
    if (!nearest || distance < nearest.distance) nearest = { reference, distance, ratio: days / reference.days };
  }

  // An equivalence beats a multiple whenever one is close enough to be true.
  if (nearest.ratio <= ABOUT_TOLERANCE && nearest.ratio >= 1 / ABOUT_TOLERANCE) {
    return { reference: nearest.reference, multiple: null };
  }

  // Otherwise fall back to the longest reference the count at least doubles, so
  // the printed multiple is big enough to mean something. "1.1×" is not a fact
  // worth a line.
  for (let i = MILESTONES.length - 1; i >= 0; i--) {
    if (MILESTONES[i].days * 2 <= days) return { reference: MILESTONES[i], multiple: days / MILESTONES[i].days };
  }
  return { reference: nearest.reference, multiple: null };
}

function formatMultiple(multiple) {
  if (multiple >= 10) return `${Math.round(multiple)}×`;
  return `${multiple.toFixed(1).replace(/\.0$/, "")}×`;
}

function count(value) {
  return new Intl.NumberFormat("en-US").format(value);
}

// The reference states its own length outright rather than being compared to the
// left-hand number, and the reader draws the comparison. That keeps the claim
// true whatever the two numbers are: a near match needs no hedge, because both
// numbers are on screen and the difference is the reader's to see.
function sentence(reference) {
  const { label, verb = "ran", days } = reference;
  return `${label} ${verb} ${count(days)} days`;
}

// An exact match is worth marking, so it takes the emphatic form. The emphasis
// rides the number rather than the label: the claim is about the length, not
// about the event being whole, and a label-shaped rule would make every new row
// depend on how its first word reads.
function emphaticSentence(reference) {
  const { label, verb = "ran", days } = reference;
  return `${label} ${verb} a full ${count(days)} days`;
}

export function milestoneComparison(days) {
  const pick = pickMilestone(days);
  if (!pick) return null;
  if (pick.multiple) return `${sentence(pick.reference)}; you logged ${formatMultiple(pick.multiple)} that`;
  if (days === pick.reference.days) return emphaticSentence(pick.reference);
  return sentence(pick.reference);
}

// Both numbers, because they are different claims: "82 days of logs" reads as if
// all 82 were spent training. The comparison hangs off the days trained.
export function trainingSummary(span) {
  if (!span) return "";
  const trained = span.days === 1
    ? "trained on 1 day"
    : span.trainedDays === span.days
      ? `trained on all ${count(span.days)} days`
      : `trained on ${count(span.trainedDays)} of ${count(span.days)} days`;
  const comparison = milestoneComparison(span.trainedDays);
  return comparison ? `${trained} — ${comparison}` : trained;
}
